/**
 * PriceVerifyIdentity — Page sécurisée d'upload des pièces d'identité
 * ─────────────────────────────────────────────────────────────────────
 * Route : /verify-identity (whitelist marketing → monte l'app complète)
 * Auth : requis (redirige vers /login si pas connecté)
 * UX   : layout mobile-first Price, un upload card par kind attendu.
 *
 * Kinds :
 *   client → id_recto, id_verso
 *   driver → id_recto, id_verso, driving_license_recto/verso,
 *            insurance, carte_grise
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Upload, Shield, X, Loader2, AlertTriangle, LogOut } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KIND_LABELS = {
  id_recto: "Pièce d'identité — recto",
  id_verso: "Pièce d'identité — verso",
  driving_license_recto: "Permis de conduire — recto",
  driving_license_verso: "Permis de conduire — verso",
  insurance: "Attestation d'assurance",
  carte_grise: "Carte grise",
};

const STATUS_META = {
  unverified: {
    label: "À vérifier",
    color: "#F45B73",
    icon: <AlertTriangle className="h-5 w-5" strokeWidth={2.5} />,
    hint: "Envoie tes documents pour obtenir le badge « Identité vérifiée ».",
  },
  pending: {
    label: "En attente",
    color: "#F0A100",
    icon: <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2.5} />,
    hint: "Vos documents sont en cours de vérification par notre équipe (~24 h).",
  },
  verified: {
    label: "Identité vérifiée",
    color: "#22A85B",
    icon: <CheckCircle2 className="h-5 w-5" strokeWidth={2.5} />,
    hint: "Vous êtes vérifié ! Le badge est actif sur votre profil.",
  },
  rejected: {
    label: "Refusé",
    color: "#F45B73",
    icon: <AlertTriangle className="h-5 w-5" strokeWidth={2.5} />,
    hint: "Les documents n'ont pas été acceptés. Corrigez et renvoyez-les.",
  },
};

const MAX_FILE_BYTES = 15 * 1024 * 1024;

// Réduit les photos de téléphone (souvent 4-10 Mo) à ~2000 px JPEG.
async function compressImage(file) {
  if (!file.type.startsWith("image/") || file.size < 900 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 2000 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], (file.name || "document").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch (_e) {
    return file; // HEIC non décodable par le navigateur → on envoie l'original
  }
}

export default function PriceVerifyIdentity() {
  const { user, isReady, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState({}); // { kind: {file, preview} }
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({}); // { kind: {state: "uploading"|"done"|"error", pct, error} }

  // Si on a un token dans l'URL → mode "magic link" : pas besoin d'être
  // connecté. Sinon on exige l'auth normale.
  useEffect(() => {
    if (token) return; // magic link mode
    if (!isReady) return;
    if (!user) { navigate("/login"); return; }
  }, [isReady, user, navigate, token]);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const path = token
        ? `/price/identity/status-by-token?token=${encodeURIComponent(token)}`
        : "/price/identity/status";
      const r = await api.get(path);
      setStatus(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (token || user) loadStatus();
  }, [user, token, loadStatus]);

  const uploadOne = async (kind, file) => {
    setProgress((p) => ({ ...p, [kind]: { state: "uploading", pct: 0 } }));
    try {
      const ready = await compressImage(file);
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", ready, ready.name || `${kind}.jpg`);
      const path = token
        ? `/price/identity/upload-by-token?token=${encodeURIComponent(token)}`
        : "/price/identity/upload";
      await api.post(path, fd, {
        timeout: 180000,
        onUploadProgress: (e) => {
          if (e.total) setProgress((p) => ({ ...p, [kind]: { state: "uploading", pct: Math.round((e.loaded / e.total) * 100) } }));
        },
      });
      setProgress((p) => ({ ...p, [kind]: { state: "done", pct: 100 } }));
      return true;
    } catch (e) {
      setProgress((p) => ({ ...p, [kind]: { state: "error", error: formatApiError(e) || "Échec de l'envoi" } }));
      return false;
    }
  };

  const submit = async () => {
    const entries = Object.entries(files).filter(([k]) => progress[k]?.state !== "done");
    if (entries.length === 0) return toast.error("Ajoute au moins un document");
    setSending(true);
    const sent = [];
    for (const [kind, obj] of entries) {
      // eslint-disable-next-line no-await-in-loop
      if (await uploadOne(kind, obj.file)) sent.push(kind);
    }
    if (sent.length) {
      const path = token
        ? `/price/identity/finalize-by-token?token=${encodeURIComponent(token)}`
        : "/price/identity/finalize";
      await api.post(path, { kinds: sent }).catch(() => {});
    }
    if (sent.length === entries.length) {
      toast.success("Documents envoyés — vérification en cours ✅");
      setFiles({});
      setProgress({});
      if (!token && status?.role === "driver") { navigate("/dashboard/driver", { replace: true }); return; }
      await loadStatus();
    } else {
      toast.error(`${entries.length - sent.length} document(s) non envoyé(s) — appuie sur Réessayer`);
    }
    setSending(false);
  };

  if (loading || !status) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FFF4EE]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const meta = STATUS_META[status.status] || STATUS_META.unverified;
  const kinds = status.required_kinds || [];
  const submittedSet = new Set(status.submitted_kinds || []);
  // Envoi complet obligatoire : toutes les pièces manquantes doivent être ajoutées.
  const missingKinds = kinds.filter((k) => status.status === "rejected" || !submittedSet.has(k));
  const stillMissing = missingKinds.filter((k) => !files[k] && progress[k]?.state !== "done");
  const canSubmit = Object.keys(files).length > 0 && stillMissing.length === 0 && !sending;
  const mandatory = !token && status.role === "driver" && status.docs_ok === false;

  return (
    <div className="min-h-screen bg-[#FFF4EE] pb-24" data-testid="verify-identity-page">
      {/* Header */}
      <div className="bg-white border-b-2 border-black">
        <div className="mx-auto max-w-2xl px-4 py-4 flex items-center gap-3">
          {mandatory ? (
            <button type="button" onClick={async () => { await logout?.(); navigate("/login", { replace: true }); }}
              data-testid="verify-identity-logout" className="grid h-10 w-10 place-items-center rounded-xl bg-secondary/40 street-border-thin" aria-label="Se déconnecter">
              <LogOut className="h-4 w-4" strokeWidth={3} />
            </button>
          ) : (
            <Link to={token ? "/" : status.role === "driver" ? "/dashboard/driver" : "/dashboard/client"} data-testid="verify-identity-back" className="grid h-10 w-10 place-items-center rounded-xl bg-secondary/40 street-border-thin">
              <X className="h-4 w-4" strokeWidth={3} />
            </Link>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50">Vérification d&apos;identité</div>
            <div className="font-[Outfit] text-[17px] font-black leading-tight">
              {status.first_name ? `Salut ${status.first_name} 👋 — ` : ""}{status.role === "driver" ? "Tes documents conducteur" : "Voyager en toute confiance"}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-4 space-y-3">
        {mandatory && (
          <div className="rounded-2xl bg-accent p-4 street-border" data-testid="verify-identity-mandatory">
            <div className="font-[Outfit] text-[15px] font-black">Dernière étape de ton inscription</div>
            <div className="mt-1 text-[13px] font-medium">Envoie tes {kinds.length} documents pour accéder à ton espace conducteur.</div>
          </div>
        )}
        {/* Status card */}
        <div
          className="rounded-2xl p-4 street-border"
          style={{ background: meta.color, boxShadow: "5px 5px 0 0 #1E0910", color: "#fff" }}
          data-testid="verify-identity-status-card"
        >
          <div className="flex items-center gap-2">
            {meta.icon}
            <div className="font-[Outfit] text-[16px] font-black">{meta.label}</div>
          </div>
          <div className="mt-1.5 text-[13px] font-medium opacity-90">{meta.hint}</div>
          {status.rejection_reason && (
            <div className="mt-2 rounded-xl bg-white/20 px-3 py-2 text-[12px] font-bold">
              Motif : {status.rejection_reason}
            </div>
          )}
        </div>

        {/* Security notice */}
        <div className="rounded-2xl bg-white p-4 street-border-thin flex items-start gap-3">
          <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-emerald-100">
            <Shield className="h-5 w-5 text-emerald-600" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-[Outfit] text-[13px] font-black">Interface sécurisée</div>
            <div className="text-[12px] text-foreground/70 leading-snug">
              Vos documents sont chiffrés et transmis uniquement via cette page.
              Ne les envoyez <b>jamais</b> par email ou SMS.
            </div>
          </div>
        </div>

        {/* Upload cards */}
        {status.status === "verified" ? (
          <div className="rounded-2xl bg-white p-6 text-center street-border">
            <div className="text-5xl mb-2">🎉</div>
            <div className="font-[Outfit] text-[16px] font-black">Tous tes documents sont validés</div>
            <div className="mt-1 text-[13px] text-foreground/60">Le badge est actif sur ton profil Price.</div>
            <Link
              to={status.role === "driver" ? "/dashboard/driver" : "/dashboard/client"}
              className="mt-4 inline-block rounded-2xl bg-primary text-primary-foreground border-2 border-black px-5 py-2.5 text-[12px] font-black uppercase tracking-widest active:translate-y-[1px]"
            >
              Retour au dashboard
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {kinds.map((kind) => (
                <UploadCard
                  key={kind}
                  kind={kind}
                  label={KIND_LABELS[kind] || kind}
                  alreadySubmitted={submittedSet.has(kind)}
                  file={files[kind]}
                  progress={progress[kind]}
                  onFile={(file) => {
                    setProgress((p) => { const n = { ...p }; delete n[kind]; return n; });
                    setFiles((prev) => ({ ...prev, [kind]: { file, preview: URL.createObjectURL(file) } }));
                  }}
                  onClear={() => setFiles((prev) => {
                    const next = { ...prev };
                    if (next[kind]?.preview) URL.revokeObjectURL(next[kind].preview);
                    delete next[kind];
                    return next;
                  })}
                />
              ))}
            </div>

            {stillMissing.length > 0 && Object.keys(files).length > 0 && (
              <p className="text-center text-[12px] font-bold text-primary" data-testid="verify-identity-missing">
                Ajoute encore {stillMissing.length} document{stillMissing.length > 1 ? "s" : ""} pour pouvoir envoyer ton dossier complet.
              </p>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="verify-identity-submit"
              className="w-full h-14 rounded-2xl bg-primary text-primary-foreground border-2 border-black font-[Outfit] font-black text-[14px] uppercase tracking-widest street-shadow active:translate-y-[2px] disabled:opacity-50"
            >
              {sending ? "Envoi en cours…" : Object.values(progress).some((p) => p.state === "error") ? "Réessayer" : status.status === "rejected" ? "Renvoyer" : "Envoyer pour vérification"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function UploadCard({ kind, label, alreadySubmitted, file, progress, onFile, onClear }) {
  const inputRef = useRef(null);
  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin" data-testid={`upload-card-${kind}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-[Outfit] text-[13px] font-black leading-tight">{label}</div>
          <div className="text-[10px] font-medium text-foreground/50">
            {alreadySubmitted && !file ? "Déjà envoyé — remplace pour mettre à jour" : "Photo ou PDF (max 15 Mo)"}
          </div>
        </div>
        {alreadySubmitted && !file && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
      </div>

      {file ? (
        <div className="mt-2 rounded-xl bg-secondary/40 p-2 flex items-center gap-2">
          {file.file.type.startsWith("image/") ? (
            <img src={file.preview} alt="" className="h-14 w-14 rounded-lg object-cover border-2 border-black" />
          ) : (
            <div className="h-14 w-14 rounded-lg bg-primary/10 grid place-items-center text-[10px] font-black uppercase border-2 border-black">PDF</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-bold truncate">{file.file.name}</div>
            <div className="text-[10px] text-foreground/60">{Math.round(file.file.size / 1024)} Ko</div>
            {progress?.state === "uploading" && (
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white border border-black" data-testid={`upload-progress-${kind}`}>
                <div className="h-full bg-primary transition-[width] duration-200" style={{ width: `${progress.pct || 5}%` }} />
              </div>
            )}
            {progress?.state === "done" && <div className="text-[10px] font-black text-emerald-600" data-testid={`upload-done-${kind}`}>Envoyé ✓</div>}
            {progress?.state === "error" && <div className="text-[10px] font-black text-rose-600" data-testid={`upload-error-${kind}`}>{progress.error}</div>}
          </div>
          <button
            type="button"
            onClick={onClear}
            data-testid={`upload-clear-${kind}`}
            className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-white border-2 border-black active:translate-y-[1px]"
            aria-label="Retirer"
          >
            <X className="h-3.5 w-3.5" strokeWidth={3} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid={`upload-pick-${kind}`}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-secondary/40 py-2.5 text-[11px] font-black uppercase tracking-widest street-border-thin street-press"
        >
          <Upload className="h-4 w-4" strokeWidth={2.5} />
          {alreadySubmitted ? "Remplacer" : "Choisir un fichier"}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (f.size > MAX_FILE_BYTES && !f.type.startsWith("image/")) {
            toast.error("Fichier trop volumineux (max 15 Mo)");
            return;
          }
          onFile(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
